/*******************************************************
 * Copyright (C) 2010-Present Avant Assessment
 * All Rights Reserved
 *******************************************************/

import {
    Grid,
} from "@material-ui/core"
import * as React from "react"
import { useEffect, useState } from "react"
import {H3Text} from "../../../../styles/AvantTypography"
import { LoginDTO } from "../../../../types/types"
import { TimeUtil } from "../../../../util/TimeUtil"
import { AvantExpansionPanel } from "../../../generic-components/AvantExpansionPanel"
import { RosterDialog } from "./rostering/RosterDialog"
import { EmailRemindersDialog } from "./EmailRemindersDialog"
import { productStore } from "../../../common/products/ProductStore"
import { contentAreaStore } from "../../../common/contentArea/ContentAreaStore";
import {TeacherPermissions} from "../../../../components/admin-portal/DevTools/LoginTool";
import {SchedulingLink, showSchedulingLink} from "../../common/components/SchedulingLink";
import {RegistrationLink, showRegistrationLink} from "../../common/components/RegistrationLink";
import {EmailReminders, showEmailReminders} from "../../common/components/EmailReminders";
import {RosterTestTakers} from "../../common/components/RosterTestTakers";
import {buildProctoredTestTakerRegistrationLink} from "../../../../util/UrlUtil";
import {groupMasterStore} from "../../../common/group/GroupMasterStore";
import {PendingProctorSetup} from "./PendingProctorSetup"

/**
 * @return `true` if the login is unexpired, or if there is no expiration date.
 *
 * `false` otherwise.
 */
function loginNotExpired(login: LoginDTO): boolean {
    const expirationDate = login.loginExpires ? TimeUtil.formatMilliToMonthDayYearLong(Number(login.loginExpires)) : null
    return expirationDate === null || new Date(expirationDate) > new Date() // login has not expired -- null means forever
}

/**
 * @return `true` if the password date is unexpired, or if there is no expiration date.
 *
 * `false` otherwise.
 */
function passwordNotExpired(login: LoginDTO): boolean {
    const passwordExpirationDate = login.passwordExpires ? TimeUtil.formatMilliToMonthDayYearLong(Number(login.passwordExpires)) : null
    return passwordExpirationDate === null || new Date(passwordExpirationDate) > new Date() // password has not expired -- null means forever
}

function loginActive(login: LoginDTO): boolean {
    return (login.id !== null && login.id !== undefined) && (login.active === true)
}

function isTeacher(username: string): boolean {
    return username.endsWith("-t") ?? false
}

/** A filtering function to prevent a teacher from viewing rostered test takers not associated with their teacher code.
 *
 *  If the currentUser is not a teacher then they are a district, school, or other admin user.
 *  There is no singular test taker associated with them, so don't filter and simply return true.
 */
function matchesTeacher(login: LoginDTO) {
    const currentUserName = productStore.loginProduct?.userName ?? ""
    if (isTeacher(currentUserName)) {
        return currentUserName.includes(login.username ?? "")
    }
    // Not a teacher code. We are not restricted to showing only one associated test taker code. Simply return true.
    return true
}

function permissionsToRoster() {
    return productStore.loginProduct?.permissions?.includes(TeacherPermissions.ROSTER.valueOf()) ?? false
}

function isRostered(login: LoginDTO) {
    return login.rostered === true
}

export interface TestControlsProps {
    /** Callback used after finishing an action that may have updated backend state.
     *  In practice, a user action in a dialog such as `RosterDialog` may update backend state.
     *  We reload the new state when we close the dialog.
     *
     *  Note: This value is nullable because these dialogs are not loaded for `PLACEReportPage`, nor are they currently supported by `PLACEReportPage`.
     * */
    reloadReport?: () => void,
}

export function TestControls({...props}: TestControlsProps) {
    const [rosteredLogins, setRosteredLogins] = useState<LoginDTO[]>([])
    const [rosterOpen, setRosterOpen] = useState(false)
    const [emailRemindersOpen, setEmailRemindersOpen] = useState(false)
    const [selectedLogin, setSelectedLogin] = useState<LoginDTO | undefined>(undefined)
    const groupInfo = groupMasterStore.groupInfo

    const handleRosterStudents = (login: LoginDTO | undefined) => {
        setSelectedLogin(login)
        setRosterOpen(true)
    }

    const handleRosterClose = () => {
        setRosterOpen(false)
        props.reloadReport?.()
    }

    function handleEmailReminders(login: LoginDTO | undefined) {
        setSelectedLogin(login)
        setEmailRemindersOpen(true)
    }

    const handleEmailRemindersClose = () => {
        setEmailRemindersOpen(false)
        props.reloadReport?.()
    }

    useEffect(() => {

        // Get every login associated with the login group, and then filter to include only rostered logins that meet certain criteria.
        const allLoginsInTheGroup = groupInfo ? groupInfo.loginList : []
        const rosteredLogins: LoginDTO[] = allLoginsInTheGroup.filter((login: LoginDTO) => {
            return loginNotExpired(login) &&
            passwordNotExpired(login) &&
            loginActive(login) &&
                isRostered(login) &&
                matchesTeacher(login)
        }).sort((a: LoginDTO, b: LoginDTO) => {
            const aSortBy = (a.username) ? a.username.toLocaleUpperCase() : ""
            const bSortBy = (b.username) ? b.username.toLocaleUpperCase() : ""
            return aSortBy.localeCompare(bSortBy)
        })
        setRosteredLogins([...rosteredLogins])
    }, [])

    return (
        <>
            {(rosteredLogins.length > 0) && (
                <>
                    <AvantExpansionPanel title={"Test Controls"} >
                        <Grid container style={{padding: "20px", gap: "2rem"}}>
                            {rosteredLogins.map((login) => (
                                <Grid container key={login.id}
                                      style={{
                                          alignItems: "center",
                                          gap: "2rem",
                                      }}
                                >
                                    <H3Text>
                                        {login.username}
                                        {(login.contentAreaId && contentAreaStore.convertContentAreaIdToLanguageName(login.contentAreaId)) ? ` (${contentAreaStore.convertContentAreaIdToLanguageName(login.contentAreaId)})` : 'content area not found'}
                                    </H3Text>
                                    {props.reloadReport &&
                                        <Grid style={{display: "flex", gap: "2rem"}}>
                                            {permissionsToRoster() &&
                                                <RosterTestTakers login={login}
                                                                  handleRosterStudents={handleRosterStudents}/>
                                            }
                                            {showEmailReminders(login) &&
                                                <EmailReminders login={login}
                                                                handleEmailReminders={handleEmailReminders}/>
                                            }
                                        </Grid>
                                    }
                                    <Grid style={{display: "flex", gap: "2rem"}}>
                                        {
                                            showRegistrationLink(buildProctoredTestTakerRegistrationLink(login.id)) &&
                                            <RegistrationLink
                                                url={buildProctoredTestTakerRegistrationLink(login.id)}/>
                                        }
                                        {showSchedulingLink(login)
                                            && <SchedulingLink url={login.schedulingLink ?? ""}/>
                                            || <PendingProctorSetup/>
                                        }
                                    </Grid>
                                </Grid>
                            ))}
                        </Grid>
                    </AvantExpansionPanel>
                    <RosterDialog
                        login={selectedLogin}
                        open={rosterOpen}
                        handleRosterClose={handleRosterClose}
                    />
                    <EmailRemindersDialog
                        login={selectedLogin}
                        open={emailRemindersOpen}
                        handleEmailRemindersClose={handleEmailRemindersClose}
                    />
                </>
            )}
        </>
    )
}
